import React from "react";
import DashboardPagination from "../components/DashboardPagination";
import PropertyInlineSummary from "../components/PropertyInlineSummary";
import { IPaginationMeta, IVisitItem } from "../types/ownerDashboard.types";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";
import { buildPropertyListingView } from "../utils/rentedPropertyDisplay";

interface VisitItemsListProps {
  visits: IVisitItem[];
  variant?: "past" | "upcoming";
  pagination?: IPaginationMeta | null;
  onPageChange?: (page: number) => void;
  rentedPropertyCatalog?: RentedPropertyCatalog;
}

function parseVisitDateParts(date: string) {
  const [day, month] = date.split("/");
  const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const monthIndex = parseInt(month, 10) - 1;

  return {
    day: day?.padStart(2, "0") ?? "--",
    monthLabel: monthNames[monthIndex] ?? month,
  };
}

export default function VisitItemsList({
  visits,
  variant = "upcoming",
  pagination,
  onPageChange,
  rentedPropertyCatalog,
}: VisitItemsListProps) {
  const currentPage = pagination?.current_page ?? 1;
  const totalPages = pagination?.last_page ?? 1;

  if (visits.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <ul className="flex w-full flex-col gap-2.5">
        {visits.map((visit) => {
          const { day, monthLabel } = parseVisitDateParts(visit.date);
          const accentClass =
            variant === "past"
              ? "border-white/10 bg-[#1c2128]"
              : "border-[#6ea8ff]/25 bg-[#6ea8ff]/10";
          const detail = rentedPropertyCatalog?.findByPropertyId(visit.property_id);
          const listing = buildPropertyListingView(detail, {
            address: visit.property_address,
          });

          return (
            <li
              key={visit.schedule_id}
              className={`flex w-full items-center gap-3 rounded-2xl border p-3.5 ${accentClass}`}
            >
              <div className="flex min-w-[3.25rem] flex-col items-center rounded-xl border border-white/10 bg-[#0d1117]/60 px-2 py-1.5">
                <span className="text-lg font-bold leading-none text-[#6ea8ff]">{day}</span>
                <span className="mt-0.5 text-[0.6rem] font-semibold uppercase text-[#8b949e]">
                  {monthLabel}
                </span>
                <span className="mt-1 text-[0.7rem] font-semibold text-[#f0f6fc]">{visit.time}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-[#f0f6fc]">{visit.property_title}</p>
                <div className="mt-1">
                  <PropertyInlineSummary listing={listing} className="line-clamp-2" />
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {onPageChange && (
        <DashboardPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
